import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Container,
  DateRangePicker,
  Grid,
  Header,
  Icon,
  Link,
  LineChart,
  Popover,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  TreeView,
  type DateRangePickerProps,
  type TreeViewProps,
} from '@cloudscape-design/components';
import { fmtDateTime, fmtPowerW } from '../format';
import { makeSegmentRenderable } from '../lib/energyChartSeries';
import { usePageTitle } from '../lib/usePageTitle';
import { useNotifications } from '../notifications/useNotifications';
import {
  buildTopologyTree,
  isAggregate,
  isVacant,
  summarizeChildren,
  useTopology,
  type ChannelTreeNode,
  type SourceTreeNode,
  type TopologyTreeNode,
} from '../hooks/useTopology';
import {
  pickReadingsBucketMs,
  toRangeBounds,
  toResampledChartSegments,
  useChannelReadings,
} from '../hooks/useChannelReadings';
import type { TopologyChannel } from '../api/contracts';

// ---------------------------------------------------------------------------
// Tree rendering
// ---------------------------------------------------------------------------
//
// Cloudscape's TreeView (not Table+expandable-rows) is used deliberately:
// it draws connector lines between parent and child, which visually reads
// as containment rather than as an indented flat list a user could
// misread as summable siblings -- the exact display mistake this page
// exists to avoid repeating (see useTopology.ts's header comment).

function channelBadges(channel: TopologyChannel) {
  const badges: React.ReactNode[] = [];
  if (isVacant(channel)) {
    badges.push(
      <Badge key="vacant" color="grey">
        Vacant
      </Badge>,
    );
  }
  if (isAggregate(channel)) {
    badges.push(
      <Badge key="aggregate" color="blue">
        Aggregate (already includes children)
      </Badge>,
    );
  }
  if (channel.isEstimated) {
    badges.push(
      <Popover
        key="estimated"
        header="Estimated value"
        content={
          channel.accuracyNote ?? 'This channel is a multiplier-based approximation, not a direct measurement.'
        }
      >
        <Badge color="severity-medium">
          <Icon name="status-warning" size="small" /> Estimated
        </Badge>
      </Popover>,
    );
  }
  if (channel.role === 'balance') {
    badges.push(
      <Badge key="balance" color="severity-neutral">
        Vendor residual, not solar
      </Badge>,
    );
  }
  return badges;
}

function channelSecondaryContent(channel: TopologyChannel) {
  const parts: string[] = [];
  if (channel.breakerRef) parts.push(`Breaker ${channel.breakerRef}`);
  if (channel.multiplier !== 1) parts.push(`×${channel.multiplier} multiplier`);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function renderChannelTreeItem(
  node: ChannelTreeNode,
  onSelect: (channelId: string) => void,
): TreeViewProps.TreeItem {
  const { channel } = node;
  const boxProps = {
    ...(isAggregate(channel) && { fontWeight: 'bold' as const }),
    ...(isVacant(channel) && { color: 'text-status-inactive' as const }),
  };
  return {
    content: (
      <SpaceBetween size="xs" direction="horizontal" alignItems="center">
        <Link
          variant="secondary"
          onFollow={(e) => {
            e.preventDefault();
            onSelect(channel.id);
          }}
        >
          <Box {...boxProps}>{channel.displayName}</Box>
        </Link>
        {channelBadges(channel)}
        {node.children.length > 0 && isAggregate(channel) && (
          <Box color="text-status-inactive" fontSize="body-s">
            ({summarizeChildrenCaption(node)})
          </Box>
        )}
      </SpaceBetween>
    ),
    secondaryContent: channelSecondaryContent(channel),
    announcementLabel: channel.displayName,
  };
}

function summarizeChildrenCaption(node: ChannelTreeNode): string {
  const s = summarizeChildren(node);
  const parts = [`${s.countedActive} active`];
  if (s.vacant > 0) parts.push(`${s.vacant} vacant`);
  return parts.join(', ');
}

function renderSourceTreeItem(node: SourceTreeNode): TreeViewProps.TreeItem {
  return {
    content: (
      <Box fontWeight="bold">
        {node.source.displayName}
        <Box color="text-status-inactive" fontSize="body-s" display="inline">
          {' '}
          ({node.source.kind})
        </Box>
      </Box>
    ),
    announcementLabel: node.source.displayName,
  };
}

function getNodeId(node: TopologyTreeNode): string {
  return node.kind === 'source' ? `source:${node.source.id}` : `channel:${node.channel.id}`;
}

function getNodeChildren(node: TopologyTreeNode): TopologyTreeNode[] | undefined {
  return node.children.length > 0 ? node.children : undefined;
}

// ---------------------------------------------------------------------------
// Channel detail / chart
// ---------------------------------------------------------------------------

function findChannel(tree: SourceTreeNode[], channelId: string): TopologyChannel | undefined {
  const stack: ChannelTreeNode[] = tree.flatMap((s) => s.children);
  while (stack.length > 0) {
    const node = stack.pop() as ChannelTreeNode;
    if (node.channel.id === channelId) return node.channel;
    stack.push(...node.children);
  }
  return undefined;
}

function isValidRange(value: DateRangePickerProps.Value | null): DateRangePickerProps.ValidationResult {
  if (value === null || value.type !== 'absolute') return { valid: true };
  if (!value.startDate || !value.endDate) {
    return { valid: false, errorMessage: 'Both start and end dates are required.' };
  }
  if (new Date(value.startDate) > new Date(value.endDate)) {
    return { valid: false, errorMessage: 'Start date must be before end date.' };
  }
  return { valid: true };
}

function ChannelDetail({ channel }: { channel: TopologyChannel }) {
  const { notifyError } = useNotifications();
  const [range, setRange] = useState<DateRangePickerProps.Value | null>(null);
  // Computed ONCE per mount, not on every render -- see toRangeBounds's doc
  // comment for why a fresh `new Date()` per render broke this page outright
  // (every render produced a new query key, so no fetch ever got the chance
  // to resolve before being replaced by the next one).
  const now = useMemo(() => new Date(), []);
  const { from, to } = useMemo(() => toRangeBounds(range, now), [range, now]);

  const readingsQuery = useChannelReadings(channel.id, from, to);

  useEffect(() => {
    if (readingsQuery.isError) notifyError(readingsQuery.error, `load readings for ${channel.displayName}`);
  }, [readingsQuery.isError, readingsQuery.error, notifyError, channel.displayName]);

  const segments = useMemo(
    () =>
      readingsQuery.data ? toResampledChartSegments(readingsQuery.data.points, pickReadingsBucketMs(from, to)) : [],
    [readingsQuery.data, from, to],
  );

  const vacant = isVacant(channel);

  return (
    <SpaceBetween size="l">
      <Header
        variant="h2"
        description={channelSecondaryContent(channel)}
        actions={<SpaceBetween size="xs" direction="horizontal">{channelBadges(channel)}</SpaceBetween>}
      >
        {channel.displayName}
      </Header>

      {vacant && (
        <Alert type="warning" header="This channel is vacant">
          It does not represent a live circuit right now -- the equipment that used to sit here has been
          moved or removed, and the vendor app still shows the old label (this is our own recorded state,
          re-verified against the physical panel, not inherited blindly from the vendor). Any watts shown
          below are historical only and must not be treated as current draw.
        </Alert>
      )}

      {isAggregate(channel) && (
        <Alert type="info" header="This is an aggregate, not an additional load">
          Its value is already represented by its child channels in the tree. Do not add this channel's
          watts to its children's watts -- that would double-count the same physical power.
        </Alert>
      )}

      {channel.role === 'balance' && (
        <Alert type="info" header="Balance is a vendor-computed residual, not solar or generation">
          This channel is Emporia&apos;s <code>mains − Σ(branches)</code> residual. This site has no solar
          on the main panel (solar sits behind a sub-panel that cannot back-feed), so a negative Balance is
          NOT generation -- it is a measurement artifact of the ×2 leg-doubling multiplier used on 240V
          circuits (worst on the dryer and oven/stove, which pair a 240V element with an unbalanced 120V
          motor/controls leg). Treat this as a signed tolerance band, never a physical quantity.
        </Alert>
      )}

      {channel.isEstimated && channel.accuracyNote && (
        <Alert type="info" header="Why this value is estimated">
          {channel.accuracyNote}
        </Alert>
      )}

      <Container
        header={
          <Header variant="h3">
            Time series
          </Header>
        }
      >
        <SpaceBetween size="m">
          <DateRangePicker
            value={range}
            onChange={({ detail }) => setRange(detail.value)}
            relativeOptions={[
              { key: 'last-24-hours', amount: 24, unit: 'hour', type: 'relative' },
              { key: 'last-48-hours', amount: 48, unit: 'hour', type: 'relative' },
              { key: 'last-7-days', amount: 7, unit: 'day', type: 'relative' },
            ]}
            isValidRange={isValidRange}
            placeholder="Last 24 hours"
            i18nStrings={{
              todayAriaLabel: 'Today',
              clearButtonLabel: 'Clear',
              cancelButtonLabel: 'Cancel',
              applyButtonLabel: 'Apply',
            }}
          />

          {readingsQuery.isLoading ? (
            <Spinner />
          ) : (
            <LineChart
              series={segments.map((segment, i) => ({
                title: i === 0 ? channel.displayName : `${channel.displayName} (cont.)`,
                type: 'line' as const,
                data: makeSegmentRenderable(segment),
                color: '#0972d3',
                valueFormatter: (v: number) => fmtPowerW(v),
              }))}
              xScaleType="time"
              xTitle="Time"
              yTitle="Watts"
              yTickFormatter={(v) => fmtPowerW(v)}
              xTickFormatter={(v) => fmtDateTime(v as unknown as Date)}
              height={300}
              hideFilter
              empty={
                <Box textAlign="center" color="inherit">
                  No readings for this range.
                </Box>
              }
            />
          )}

          <Box color="text-status-inactive" fontSize="body-s">
            Gaps in the line indicate periods with no data (sampleCount = 0) -- they are never drawn as
            zero.
          </Box>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Circuits() {
  usePageTitle('Circuits');
  const { notifyError } = useNotifications();

  const topologyQuery = useTopology();
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  useEffect(() => {
    if (topologyQuery.isError) notifyError(topologyQuery.error, 'load circuit topology');
  }, [topologyQuery.isError, topologyQuery.error, notifyError]);

  const tree = useMemo(
    () => (topologyQuery.data ? buildTopologyTree(topologyQuery.data) : []),
    [topologyQuery.data],
  );

  // Expand every source by default so the top-level structure (which
  // sources exist) is visible without an extra click; individual channel
  // subtrees stay collapsed until the user opts in.
  useEffect(() => {
    if (tree.length > 0 && expandedItems.length === 0) {
      setExpandedItems(tree.map((s) => getNodeId(s)));
    }
  }, [tree, expandedItems.length]);

  const selectedChannel = selectedChannelId ? findChannel(tree, selectedChannelId) : undefined;

  function renderItem(node: TopologyTreeNode): TreeViewProps.TreeItem {
    if (node.kind === 'source') return renderSourceTreeItem(node);
    return renderChannelTreeItem(node, setSelectedChannelId);
  }

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Panel topology, source by source. Select a channel to see its time series."
      >
        Circuits
      </Header>

      <Grid gridDefinition={[{ colspan: { default: 12, xs: 5 } }, { colspan: { default: 12, xs: 7 } }]}>
        <Container header={<Header variant="h2">Topology</Header>}>
          {topologyQuery.isLoading ? (
            <Spinner />
          ) : tree.length === 0 ? (
            <Box textAlign="center" color="inherit">
              No topology data.
            </Box>
          ) : (
            <TreeView<TopologyTreeNode>
              items={tree}
              renderItem={renderItem}
              getItemId={getNodeId}
              getItemChildren={getNodeChildren}
              expandedItems={expandedItems}
              onItemToggle={({ detail }) => {
                setExpandedItems((prev) =>
                  detail.expanded ? [...prev, detail.id] : prev.filter((id) => id !== detail.id),
                );
              }}
              ariaLabel="Circuit topology"
            />
          )}
        </Container>

        <Container header={<Header variant="h2">Channel detail</Header>}>
          {selectedChannel ? (
            <ChannelDetail channel={selectedChannel} />
          ) : (
            <Box textAlign="center" color="inherit">
              <StatusIndicator type="info">Select a channel from the tree to view its time series.</StatusIndicator>
            </Box>
          )}
        </Container>
      </Grid>
    </SpaceBetween>
  );
}
